import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { CourseSelect, SessionSelect } from '../../components/MasterDataSelect';
import { getStudentsByCourseSession, transferStudents } from '../../utils/studentsApi';
import { showSuccess, showError } from '../../components/CustomAlert';
import { fetchMasterData } from '../../store/slices/masterDataSlice';
import { FaExchangeAlt, FaUsers, FaSearch, FaCheckCircle } from 'react-icons/fa';

const Container = styled.div`
  padding: 30px;
  min-height: 100vh;
  background: #f8f9fa;
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #2c3e50;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const Subtitle = styled.p`
  color: #6c757d;
  font-size: 1rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  padding: 30px;
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 1.3rem;
  color: #34495e;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
  font-size: 0.9rem;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  color: #333;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  color: #333;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
  }
`;

const Button = styled.button`
  background: ${props => props.variant === 'primary' ? 'rgb(51, 66, 147)' : props.variant === 'success' ? '#28a745' : '#6c757d'};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  font-size: 1rem;

  &:hover {
    background: ${props => props.variant === 'primary' ? 'rgb(11, 23, 92)' : props.variant === 'success' ? '#218838' : '#5a6268'};
    transform: translateY(-2px);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const StudentsTable = styled.div`
  margin-top: 20px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  background: #f8f9fa;
  padding: 15px;
  display: grid;
  grid-template-columns: 50px 2fr 2fr 1.5fr 100px;
  gap: 15px;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
`;

const TableRow = styled.div`
  padding: 15px;
  display: grid;
  grid-template-columns: 50px 2fr 2fr 1.5fr 100px;
  gap: 15px;
  align-items: center;
  border-bottom: 1px solid #e9ecef;
  transition: background 0.2s ease;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const EmptyState = styled.div`
  padding: 40px;
  text-align: center;
  color: #6c757d;
  font-size: 1.1rem;
`;

const InfoBox = styled.div`
  background: #e7f3ff;
  border-left: 4px solid #2196f3;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  color: #0c5460;
`;

const SelectionSummary = styled.div`
  background: #f0f8ff;
  border: 2px solid #4a90e2;
  border-radius: 8px;
  padding: 15px 20px;
  margin: 20px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  color: #2c3e50;
`;

const Badge = styled.span`
  background: ${props => props.type === 'success' ? '#28a745' : '#007bff'};
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
`;

const StudentTransfer = () => {
  const dispatch = useDispatch();
  
  // Source selection
  const [sourceCourseId, setSourceCourseId] = useState('');
  const [sourceSessionId, setSourceSessionId] = useState('');
  
  // Destination selection
  const [destCourseId, setDestCourseId] = useState('');
  const [destSessionId, setDestSessionId] = useState('');
  
  // Transfer details
  const [transferReason, setTransferReason] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  
  // Students and selection
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Loading and state
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);

  // Load students when source course and session are selected
  const handleLoadStudents = async () => {
    if (!sourceCourseId || !sourceSessionId) {
      showError('Please select both course and session');
      return;
    }

    setLoading(true);
    try {
      const response = await getStudentsByCourseSession(sourceCourseId, sourceSessionId);
      setStudents(response.students || []);
      setSelectedStudents([]);
      setSelectAll(false);
      
      if (response.students && response.students.length > 0) {
        showSuccess(`Loaded ${response.students.length} student(s)`);
      } else {
        showError('No students found in this course and session');
      }
    } catch (error) {
      showError(error.message || 'Failed to load students');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle select all checkbox
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedStudents(students.map(s => s.id));
    } else {
      setSelectedStudents([]);
    }
  };

  // Handle individual student selection
  const handleSelectStudent = (studentId, checked) => {
    if (checked) {
      setSelectedStudents([...selectedStudents, studentId]);
    } else {
      setSelectedStudents(selectedStudents.filter(id => id !== studentId));
      setSelectAll(false);
    }
  };

  // Handle transfer
  const handleTransfer = async () => {
    if (selectedStudents.length === 0) {
      showError('Please select at least one student to transfer');
      return;
    }

    if (!destCourseId || !destSessionId) {
      showError('Please select destination course and session');
      return;
    }

    if (sourceCourseId === destCourseId && sourceSessionId === destSessionId) {
      showError('Source and destination cannot be the same');
      return;
    }

    setTransferring(true);
    try {
      const response = await transferStudents(
        selectedStudents,
        destCourseId,
        destSessionId,
        transferReason,
        transferNotes
      );
      
      showSuccess(response.message || 'Students transferred successfully');
      
      // Refresh the Redux store to update student data globally
      await dispatch(fetchMasterData());
      
      // Reload students list to reflect changes
      await handleLoadStudents();
      
      // Clear destination selection and transfer details
      setDestCourseId('');
      setDestSessionId('');
      setTransferReason('');
      setTransferNotes('');
    } catch (error) {
      showError(error.message || 'Failed to transfer students');
    } finally {
      setTransferring(false);
    }
  };

  // Update selectAll when all students are manually selected
  useEffect(() => {
    if (students.length > 0 && selectedStudents.length === students.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedStudents, students]);

  return (
    <Container>
      <Header>
        <Title>
          <FaExchangeAlt />
          Student Transfer
        </Title>
        <Subtitle>
          Transfer students from one course and session to another while preserving their attendance history
        </Subtitle>
      </Header>

      <InfoBox>
        <strong>Important:</strong> When you transfer students, their course and session will be updated, but all their previous attendance records will be preserved with the original course/session information.
      </InfoBox>

      {/* Source Course & Session */}
      <Card>
        <SectionTitle>
          <FaSearch />
          1. Select Source Course & Session
        </SectionTitle>
        
        <FormRow>
          <FormGroup>
            <Label>Source Course</Label>
            <CourseSelect
              value={sourceCourseId}
              onChange={setSourceCourseId}
              placeholder="Select source course"
            />
          </FormGroup>

          <FormGroup>
            <Label>Source Session</Label>
            <SessionSelect
              value={sourceSessionId}
              onChange={setSourceSessionId}
              courseId={sourceCourseId}
              placeholder="Select source session"
              isDisabled={!sourceCourseId}
            />
          </FormGroup>
        </FormRow>

        <Button
          variant="primary"
          onClick={handleLoadStudents}
          disabled={!sourceCourseId || !sourceSessionId || loading}
        >
          <FaUsers />
          {loading ? 'Loading...' : 'Load Students'}
        </Button>

        {/* Students List */}
        {students.length > 0 && (
          <>
            <SelectionSummary>
              <span>
                <FaUsers style={{ marginRight: '8px' }} />
                {selectedStudents.length} of {students.length} student(s) selected
              </span>
              <Badge type={selectedStudents.length > 0 ? 'success' : 'info'}>
                {selectedStudents.length}
              </Badge>
            </SelectionSummary>

            <StudentsTable>
              <TableHeader>
                <Checkbox
                  type="checkbox"
                  checked={selectAll}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
                <div>Name</div>
                <div>Email</div>
                <div>Phone</div>
                <div>Status</div>
              </TableHeader>

              {students.map((student) => (
                <TableRow key={student.id}>
                  <Checkbox
                    type="checkbox"
                    checked={selectedStudents.includes(student.id)}
                    onChange={(e) => handleSelectStudent(student.id, e.target.checked)}
                  />
                  <div>{`${student.firstName} ${student.lastName}`}</div>
                  <div>{student.email}</div>
                  <div>{student.phone || 'N/A'}</div>
                  <div>
                    <Badge type={student.status === 'Active' ? 'success' : 'info'}>
                      {student.status || 'Active'}
                    </Badge>
                  </div>
                </TableRow>
              ))}
            </StudentsTable>
          </>
        )}

        {students.length === 0 && !loading && (
          <EmptyState>
            Select a course and session, then click "Load Students" to view students
          </EmptyState>
        )}
      </Card>

      {/* Destination Course & Session */}
      <Card>
        <SectionTitle>
          <FaExchangeAlt />
          2. Select Destination Course & Session
        </SectionTitle>

        <FormRow>
          <FormGroup>
            <Label>Destination Course</Label>
            <CourseSelect
              value={destCourseId}
              onChange={setDestCourseId}
              placeholder="Select destination course"
            />
          </FormGroup>

          <FormGroup>
            <Label>Destination Session</Label>
            <SessionSelect
              value={destSessionId}
              onChange={setDestSessionId}
              courseId={destCourseId}
              placeholder="Select destination session"
              isDisabled={!destCourseId}
            />
          </FormGroup>
        </FormRow>

        <FormRow>
          <FormGroup>
            <Label>Transfer Reason</Label>
            <Select
              value={transferReason}
              onChange={(e) => setTransferReason(e.target.value)}
            >
              <option value="">--Select Reason--</option>
              <option value="Course Completion">Course Completion</option>
              <option value="Schedule Change">Schedule Change</option>
              <option value="Student Request">Student Request</option>
              <option value="Administrative">Administrative</option>
              <option value="Other">Other</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Transfer Notes (Optional)</Label>
            <TextArea
              value={transferNotes}
              onChange={(e) => setTransferNotes(e.target.value)}
              placeholder="Enter any additional notes about this transfer..."
            />
          </FormGroup>
        </FormRow>
      </Card>

      {/* Transfer Action */}
      <Card>
        <SectionTitle>
          <FaCheckCircle />
          3. Transfer Students
        </SectionTitle>

        <Button
          variant="success"
          onClick={handleTransfer}
          disabled={
            selectedStudents.length === 0 ||
            !destCourseId ||
            !destSessionId ||
            transferring
          }
          style={{ fontSize: '1.1rem', padding: '15px 30px' }}
        >
          <FaExchangeAlt />
          {transferring
            ? 'Transferring...'
            : `Transfer ${selectedStudents.length} Selected Student(s)`}
        </Button>
      </Card>
    </Container>
  );
};

export default StudentTransfer;

